import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import { useProjectContext } from "@/hooks/useProjectContext";
import Lucide from "@/components/Base/Lucide";
import { FormSelect, FormInput } from "@/components/Base/Form";
import Litepicker from "@/components/Base/Litepicker";
import { Menu, Dialog } from "@/components/Base/Headless";
import Button from "@/components/Base/Button";
import { getToken, getRole } from "@/utils/auth";
import clsx from "clsx";
import _ from "lodash";
import dayjs from "dayjs";

type OutletGroup = {
    id: string;
    groupName: string;
    companyId?: string;
    createdAt: string;
    updatedAt: string;
};

type Outlet = {
    id: string;
    name: string;
    outletGroup?: string;
    [key: string]: any;
};

type CheckInOut = {
    id: string;
    outletId: string | null;
    userId: string | null;
    teamId: string | null;
    checkInAt: string | null;
    checkInPosition: string | null;
    photoUrl: string | null;
    shift: string | null;
    projectId: string | null;
    team?: {
        id: string;
        name: string;
    } | null;
    user?: {
        id: string;
        firstName: string | null;
        lastName: string | null;
        mobile: string | null;
    } | null;
    outlet?: {
        id: string;
        name: string;
    } | null;
    [key: string]: any;
};

export default function Attendance() {
    const { projectId } = useParams<{ projectId: string }>();
    const { projectName } = useProjectContext();
    const [searchParams] = useSearchParams();
    const [generalReportFilter, setGeneralReportFilter] = useState<string>(dayjs().format("D MMM, YYYY"));
    const [outletGroups, setOutletGroups] = useState<OutletGroup[]>([]);
    const [outlets, setOutlets] = useState<Outlet[]>([]);
    const [checkIns, setCheckIns] = useState<CheckInOut[]>([]);
    const [teams, setTeams] = useState<Map<string, { id: string; name: string }>>(new Map());
    const [loadingOutletGroups, setLoadingOutletGroups] = useState(true);
    const [loadingOutlets, setLoadingOutlets] = useState(true);
    const [loadingCheckIns, setLoadingCheckIns] = useState(true);
    const [filterQuery, setFilterQuery] = useState<string>("");
    const [sortField, setSortField] = useState<'groupName' | 'createdAt' | 'updatedAt'>('groupName');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [selectedCheckInImage, setSelectedCheckInImage] = useState<string | null>(null);
    const [selectedCheckInRecord, setSelectedCheckInRecord] = useState<CheckInOut | null>(null);
    const [selectedUser, setSelectedUser] = useState<{ firstName: string | null; lastName: string | null } | null>(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [showShiftTimeModal, setShowShiftTimeModal] = useState(false);
    const [shiftTimes, setShiftTimes] = useState<Array<{ id: string; time: string; createdAt: string }>>([]);
    const [loadingShiftTimes, setLoadingShiftTimes] = useState(false);
    const [newShiftTime, setNewShiftTime] = useState({ hours: '09', minutes: '30', period: 'AM' });
    const [addingShiftTime, setAddingShiftTime] = useState(false);
    const role = getRole();

    // Fetch outlet groups
    useEffect(() => {
        (async () => {
            try {
                setLoadingOutletGroups(true);
                const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
                const companyId = searchParams.get("companyId");
                const queryParams = new URLSearchParams();
                
                // For SUPERADMIN, add companyId if provided in URL
                // For ADMIN, backend will automatically filter by their companyId
                if (role === "SUPERADMIN" && companyId) {
                    queryParams.append("companyId", companyId);
                }

                const res = await fetch(
                    `${apiUrl}/outlet-groups?${queryParams.toString()}`,
                    {
                        headers: { Authorization: `Bearer ${getToken()}` }
                    }
                );
                const data = await res.json();
                setOutletGroups(Array.isArray(data?.data) ? data.data : []);
            } catch (err) {
                console.error("Failed to fetch outlet groups:", err);
            } finally {
                setLoadingOutletGroups(false);
            }
        })();
    }, [searchParams, role]);

    // Fetch outlets - paginate through all pages to get all outlets
    useEffect(() => {
        (async () => {
            try {
                setLoadingOutlets(true);
                const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
                const companyId = searchParams.get("companyId");
                const token = getToken();
                
                // Fetch ALL outlets by paginating through all pages
                let allOutlets: any[] = [];
                let currentPage = 1;
                let hasMorePages = true;
                const pageSize = 100; // Use MAX_PAGE_SIZE to minimize API calls

                while (hasMorePages) {
                    const queryParams = new URLSearchParams();
                    
                    // For SUPERADMIN, add companyId if provided in URL
                    // For ADMIN, backend will automatically filter by their companyId
                    if (role === "SUPERADMIN" && companyId) {
                        queryParams.append("companyId", companyId);
                    }
                    queryParams.append("pageSize", pageSize.toString());
                    queryParams.append("page", currentPage.toString());

                    const res = await fetch(
                        `${apiUrl}/outlets?${queryParams.toString()}`,
                        {
                            headers: { Authorization: `Bearer ${token}` }
                        }
                    );
                    const data = await res.json();
                    const pageOutlets = Array.isArray(data?.data) ? data.data : [];
                    
                    if (pageOutlets.length > 0) {
                        allOutlets = [...allOutlets, ...pageOutlets];
                        const totalPages = data.totalPages || 1;
                        hasMorePages = currentPage < totalPages;
                        currentPage++;
                    } else {
                        hasMorePages = false;
                    }
                }

                setOutlets(allOutlets);
                console.log(`Fetched ${allOutlets.length} total outlets`);
            } catch (err) {
                console.error("Failed to fetch outlets:", err);
                setOutlets([]);
            } finally {
                setLoadingOutlets(false);
            }
        })();
    }, [searchParams, role]);

    // Fetch check-ins for the selected date
    useEffect(() => {
        if (!projectId) return;

        (async () => {
            try {
                setLoadingCheckIns(true);
                const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
                const token = getToken();
                
                // Parse the selected date and convert to ISO format (YYYY-MM-DD) for backend
                let selectedDate = dayjs(generalReportFilter, "D MMM, YYYY");
                if (!selectedDate.isValid()) {
                    selectedDate = dayjs(generalReportFilter);
                }
                if (!selectedDate.isValid()) {
                    console.error('Invalid date format:', generalReportFilter);
                    setCheckIns([]);
                    setLoadingCheckIns(false);
                    return;
                }
                const dateParam = selectedDate.format('YYYY-MM-DD');
                
                // Fetch check-ins for the selected date by paginating through all pages
                // Backend now supports date filtering, so we only fetch one day's data
                let allCheckIns: any[] = [];
                let currentPage = 1;
                let hasMorePages = true;
                const pageSize = 100; // Use MAX_PAGE_SIZE to minimize API calls

                while (hasMorePages) {
                const queryParams = new URLSearchParams();
                queryParams.append("projectId", projectId);
                    queryParams.append("date", dateParam); // Add date filter to backend
                    queryParams.append("pageSize", pageSize.toString());
                    queryParams.append("page", currentPage.toString());

                const res = await fetch(
                    `${apiUrl}/check-in?${queryParams.toString()}`,
                    {
                            headers: { Authorization: `Bearer ${token}` }
                    }
                );
                const data = await res.json();
                    const pageCheckIns = Array.isArray(data?.data) ? data.data : [];
                    
                    if (pageCheckIns.length > 0) {
                        allCheckIns = [...allCheckIns, ...pageCheckIns];
                        const totalPages = data.totalPages || 1;
                        hasMorePages = currentPage <= totalPages;
                        currentPage++;
                    } else {
                        hasMorePages = false;
                    }
                }

                console.log('Fetched check-ins:', allCheckIns.length, 'total');
                if (allCheckIns.length > 0) {
                    console.log('Sample check-in:', allCheckIns[0]);
                    console.log('Has team?', !!allCheckIns[0].team);
                }
                setCheckIns(allCheckIns);

                // Populate teams map from check-ins response for fallback lookup
                // This allows check-ins with teamId but missing team relation to still be displayed
                const teamsMap = new Map<string, { id: string; name: string }>();
                allCheckIns.forEach(checkIn => {
                    if (checkIn.team && checkIn.teamId) {
                        teamsMap.set(checkIn.teamId, {
                            id: checkIn.team.id,
                            name: checkIn.team.name
                        });
                    }
                });
                setTeams(teamsMap);
                console.log('Populated teams map:', teamsMap.size, 'teams');
            } catch (err) {
                console.error("Failed to fetch check-ins:", err);
                setCheckIns([]);
            } finally {
                setLoadingCheckIns(false);
            }
        })();
    }, [projectId, generalReportFilter]); // Add generalReportFilter to dependencies to refetch when date changes

    // Fetch user data when modal opens if user relation is missing
    useEffect(() => {
        if (showImageModal && selectedCheckInRecord && selectedCheckInRecord.userId && !selectedCheckInRecord.user) {
            (async () => {
                try {
                    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
                    const res = await fetch(
                        `${apiUrl}/users/${selectedCheckInRecord.userId}`,
                        {
                            headers: { Authorization: `Bearer ${getToken()}` }
                        }
                    );
                    const data = await res.json();
                    const userData = data?.data || data;
                    if (userData) {
                        setSelectedUser({
                            firstName: userData.firstName || null,
                            lastName: userData.lastName || null
                        });
                    }
                } catch (err) {
                    console.error("Failed to fetch user:", err);
                }
            })();
        } else if (selectedCheckInRecord?.user) {
            // If user relation exists, use it
            setSelectedUser({
                firstName: selectedCheckInRecord.user.firstName,
                lastName: selectedCheckInRecord.user.lastName
            });
        } else {
            setSelectedUser(null);
        }
    }, [showImageModal, selectedCheckInRecord]);

    // Fetch shift times
    useEffect(() => {
        if (showShiftTimeModal && projectId) {
            fetchShiftTimes();
        }
    }, [showShiftTimeModal, projectId]);

    const fetchShiftTimes = async () => {
        try {
            setLoadingShiftTimes(true);
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const queryParams = new URLSearchParams();
            queryParams.append('projectId', projectId || '');
            queryParams.append('pageSize', '100');

            const res = await fetch(
                `${apiUrl}/shift-times?${queryParams.toString()}`,
                {
                    headers: { Authorization: `Bearer ${getToken()}` }
                }
            );
            const data = await res.json();
            setShiftTimes(Array.isArray(data?.data) ? data.data : []);
        } catch (err) {
            console.error("Failed to fetch shift times:", err);
            setShiftTimes([]);
        } finally {
            setLoadingShiftTimes(false);
        }
    };

    const handleAddShiftTime = async () => {
        try {
            const hours = parseInt(newShiftTime.hours);
            const minutes = parseInt(newShiftTime.minutes);
            
            if (isNaN(hours) || isNaN(minutes) || hours < 1 || hours > 12 || minutes < 0 || minutes > 59) {
                alert('Please enter a valid time');
                return;
            }

            const timeString = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${newShiftTime.period}`;
            
            setAddingShiftTime(true);
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const res = await fetch(`${apiUrl}/shift-times`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${getToken()}`
                },
                body: JSON.stringify({
                    time: timeString,
                    projectId: projectId || null
                })
            });

            if (!res.ok) {
                const error = await res.json();
                throw new Error(error.error || 'Failed to add shift time');
            }

            // Reset form
            setNewShiftTime({ hours: '09', minutes: '30', period: 'AM' });
            // Refresh shift times
            await fetchShiftTimes();
        } catch (err: any) {
            console.error("Failed to add shift time:", err);
            alert(err.message || 'Failed to add shift time');
        } finally {
            setAddingShiftTime(false);
        }
    };

    const handleDeleteShiftTime = async (id: string) => {
        if (!confirm('Are you sure you want to delete this shift time?')) return;

        try {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const res = await fetch(`${apiUrl}/shift-times/${id}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${getToken()}`
                }
            });

            if (!res.ok) {
                const errorText = await res.text().catch(() => 'Unknown error');
                console.error("Delete failed with status:", res.status, errorText);
                throw new Error(`Failed to delete shift time: ${res.status} ${errorText}`);
            }

            // Success - 204 No Content is expected for delete
            console.log("Shift time deleted successfully, refreshing list...");
            
            // Refresh shift times
            await fetchShiftTimes();
        } catch (err) {
            console.error("Failed to delete shift time:", err);
            alert(`Failed to delete shift time: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
    };

    // Get check-ins grouped by outlet and team, counting users per team per outlet
    const outletTeamUserCounts = React.useMemo((): Map<string, Map<string, { count: number; teamName: string; userCheckInData: Map<string, { time: string; isOnTime: boolean; isOutOfRange: boolean; checkInRecord: CheckInOut }> }>> => {
        if (!generalReportFilter || !checkIns.length) return new Map<string, Map<string, { count: number; teamName: string; userCheckInData: Map<string, { time: string; isOnTime: boolean; isOutOfRange: boolean; checkInRecord: CheckInOut }> }>>();

        // Parse the selected date - try multiple formats
        let selectedDate = dayjs(generalReportFilter, "D MMM, YYYY");
        if (!selectedDate.isValid()) {
            selectedDate = dayjs(generalReportFilter);
        }
        if (!selectedDate.isValid()) return new Map<string, Map<string, { count: number; teamName: string; userCheckInData: Map<string, { time: string; isOnTime: boolean; isOutOfRange: boolean; checkInRecord: CheckInOut }> }>>();

        // Filter check-ins for the selected date
        const filteredCheckIns = checkIns.filter((checkIn) => {
            if (!checkIn.outletId || !checkIn.checkInAt || !checkIn.teamId) return false;
            
            // If team relation is missing, try to get it from the teams map
            if (!checkIn.team && checkIn.teamId) {
                const teamFromMap = teams.get(checkIn.teamId);
                if (teamFromMap) {
                    checkIn.team = teamFromMap;
                } else {
                    // Still can't find team, skip this check-in
                    console.warn('Check-in missing team relation:', checkIn.id, 'teamId:', checkIn.teamId);
                    return false;
                }
            }
            
            if (!checkIn.team) return false;
            
            const checkInDate = dayjs(checkIn.checkInAt);
            return checkInDate.isSame(selectedDate, 'day');
        });

        // Group by outlet, then by team, counting unique users
        // Structure: Map<outletId, Map<teamId, userCount>>
        const result = new Map<string, Map<string, { count: number; teamName: string }>>();
        
        filteredCheckIns.forEach((checkIn) => {
            if (!checkIn.outletId || !checkIn.teamId || !checkIn.userId || !checkIn.team) return;
            
            const outletId = checkIn.outletId;
            const teamId = checkIn.teamId;
            const teamName = checkIn.team.name;

            if (!result.has(outletId)) {
                result.set(outletId, new Map());
            }
            
            const outletTeams = result.get(outletId)!;
            if (!outletTeams.has(teamId)) {
                outletTeams.set(teamId, { count: 0, teamName });
            }
            
            // Count unique users per team per outlet
            // We'll need to track users separately, so let's use a different approach
        });

        // Helper function to calculate distance between two GPS coordinates using Haversine formula
        const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
            const R = 6371; // Earth's radius in kilometers
            const dLat = (lat2 - lat1) * Math.PI / 180;
            const dLon = (lon2 - lon1) * Math.PI / 180;
            const a = 
                Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                Math.sin(dLon / 2) * Math.sin(dLon / 2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            return R * c; // Distance in kilometers
        };

        // Better approach: track users per team per outlet with check-in time and status
        // Structure: Map<outletId, Map<teamId, Map<userId, { checkInTime: string; isOnTime: boolean; isOutOfRange: boolean; checkInRecord: CheckInOut }>>>
        const userTracking = new Map<string, Map<string, Map<string, { checkInTime: string; isOnTime: boolean; isOutOfRange: boolean; checkInRecord: CheckInOut }>>>();
        filteredCheckIns.forEach((checkIn) => {
            if (!checkIn.outletId || !checkIn.teamId || !checkIn.userId || !checkIn.team || !checkIn.checkInAt) return;
            
            const outletId = checkIn.outletId;
            const teamId = checkIn.teamId;
            const userId = checkIn.userId;

            if (!userTracking.has(outletId)) {
                userTracking.set(outletId, new Map());
            }
            
            const outletTeams = userTracking.get(outletId)!;
            if (!outletTeams.has(teamId)) {
                outletTeams.set(teamId, new Map());
            }
            
            const teamUsers = outletTeams.get(teamId)!;
            
            // Determine if check-in is on time or late
            // If shift exists, compare checkInAt with shift
            // If checkInAt <= shift: on time (green), else late (orange)
            let isOnTime = true; // Default to on-time if no shift is set
            if (checkIn.shift) {
                const checkInTime = dayjs(checkIn.checkInAt);
                const shiftTime = dayjs(checkIn.shift);
                // Check if check-in time is before or equal to shift time
                isOnTime = checkInTime.isBefore(shiftTime, 'minute') || checkInTime.isSame(shiftTime, 'minute');
            }
            
            // Calculate distance between check-in position and outlet GPS coordinates
            let isOutOfRange = false;
            if (checkIn.checkInPosition && checkIn.outletId) {
                try {
                    // Parse checkInPosition (format: "lat,lng")
                    const [checkInLat, checkInLng] = checkIn.checkInPosition.split(',').map(coord => parseFloat(coord.trim()));
                    
                    // Find the outlet to get its GPS coordinates
                    const outlet = outlets.find(o => o.id === outletId);
                    if (outlet && outlet.gpsLat && outlet.gpsLong && 
                        !isNaN(checkInLat) && !isNaN(checkInLng) &&
                        !isNaN(outlet.gpsLat) && !isNaN(outlet.gpsLong)) {
                        const distance = calculateDistance(
                            checkInLat,
                            checkInLng,
                            outlet.gpsLat,
                            outlet.gpsLong
                        );
                        // If distance is more than 2km, mark as out of range
                        isOutOfRange = distance > 2;
                    }
                } catch (error) {
                    console.error('Error calculating distance:', error);
                }
            }
            
            // Store the user with their check-in time and status (only keep first check-in if multiple)
            if (!teamUsers.has(userId)) {
                // Format check-in time as HH:mm
                const checkInTimeFormatted = dayjs(checkIn.checkInAt).format('HH:mm');
                teamUsers.set(userId, { checkInTime: checkInTimeFormatted, isOnTime, isOutOfRange, checkInRecord: checkIn });
            }
        });

        // Convert to final structure with counts, team names, and user check-in data
        const finalResult = new Map<string, Map<string, { count: number; teamName: string; userCheckInData: Map<string, { time: string; isOnTime: boolean; isOutOfRange: boolean; checkInRecord: CheckInOut }> }>>();
        userTracking.forEach((teams, outletId) => {
            const outletData = new Map<string, { count: number; teamName: string; userCheckInData: Map<string, { time: string; isOnTime: boolean; isOutOfRange: boolean; checkInRecord: CheckInOut }> }>();
            teams.forEach((users, teamId) => {
                const checkIn = filteredCheckIns.find(ci => ci.teamId === teamId && ci.team);
                if (checkIn && checkIn.team) {
                    // Convert Map to array, sort by check-in time (earliest first), then back to Map
                    // Map preserves insertion order, so sorting before insertion ensures correct order
                    const userArray = Array.from(users.entries());
                    userArray.sort((a, b) => {
                        // Compare check-in times (HH:mm format, e.g., "09:30" < "14:45")
                        const timeA = a[1].checkInTime;
                        const timeB = b[1].checkInTime;
                        if (timeA < timeB) return -1;
                        if (timeA > timeB) return 1;
                        return 0;
                    });
                    
                    // Create a new Map with sorted order (Map preserves insertion order)
                    const userCheckInData = new Map<string, { time: string; isOnTime: boolean; isOutOfRange: boolean; checkInRecord: CheckInOut }>();
                    userArray.forEach(([userId, data]) => {
                        userCheckInData.set(userId, { time: data.checkInTime, isOnTime: data.isOnTime, isOutOfRange: data.isOutOfRange, checkInRecord: data.checkInRecord });
                    });
                    
                    outletData.set(teamId, {
                        count: users.size,
                        teamName: checkIn.team.name,
                        userCheckInData
                    });
                }
            });
            finalResult.set(outletId, outletData);
        });

        return finalResult;
    }, [checkIns, generalReportFilter, outlets, teams]);

    // Group outlets by outletGroup (include all outlets, even without a group)
    const outletsByGroup = React.useMemo(() => {
        const grouped: Record<string, Outlet[]> = {};
        outlets.forEach((outlet) => {
            const groupName = outlet.outletGroup || 'Ungrouped';
            if (!grouped[groupName]) {
                grouped[groupName] = [];
            }
            grouped[groupName].push(outlet);
        });
        return grouped;
    }, [outlets]);

    // Get team columns per outlet group
    // Only show columns for teams that have check-ins in outlets within that specific outlet group
    const teamColumnsByGroup = React.useMemo(() => {
        const result = new Map<string, Array<{ teamId: string; teamName: string; maxCount: number }>>();

        // Iterate through each outlet group
        Object.keys(outletsByGroup).forEach((groupName) => {
            const groupOutlets = outletsByGroup[groupName];
            const teamMap = new Map<string, { maxCount: number; teamName: string }>();

            // Check each outlet in this group for check-ins
            groupOutlets.forEach((outlet) => {
                const outletTeams = outletTeamUserCounts.get(outlet.id);
                if (outletTeams) {
                    outletTeams.forEach((teamData, teamId) => {
                        if (!teamMap.has(teamId)) {
                            teamMap.set(teamId, { maxCount: 0, teamName: teamData.teamName });
                        }
                        const existingTeamData = teamMap.get(teamId)!;
                        if (teamData.count > existingTeamData.maxCount) {
                            existingTeamData.maxCount = teamData.count;
                        }
                    });
                }
            });

            // Convert to array and sort by team name for consistent ordering
            const teamColumns = Array.from(teamMap.entries())
                .map(([teamId, data]) => ({
                    teamId,
                    teamName: data.teamName,
                    maxCount: data.maxCount
                }))
                .sort((a, b) => a.teamName.localeCompare(b.teamName));

            result.set(groupName, teamColumns);
        });

        return result;
    }, [outletTeamUserCounts, outletsByGroup]);

    // Calculate statistics for the selected date
    const checkInStats = React.useMemo(() => {
        if (!generalReportFilter || !checkIns.length) {
            return { total: 0, successful: 0, warnings: 0 };
        }

        // Parse the selected date
        let selectedDate = dayjs(generalReportFilter, "D MMM, YYYY");
        if (!selectedDate.isValid()) {
            selectedDate = dayjs(generalReportFilter);
        }
        if (!selectedDate.isValid()) {
            return { total: 0, successful: 0, warnings: 0 };
        }

        // Helper function to calculate distance
        const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
            const R = 6371; // Earth's radius in kilometers
            const dLat = (lat2 - lat1) * Math.PI / 180;
            const dLon = (lon2 - lon1) * Math.PI / 180;
            const a = 
                Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                Math.sin(dLon / 2) * Math.sin(dLon / 2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            return R * c;
        };

        let total = 0;
        let successful = 0;
        let warnings = 0;

        checkIns.forEach((checkIn) => {
            // Filter by date
            if (!checkIn.checkInAt) return;
            const checkInDate = dayjs(checkIn.checkInAt);
            if (!checkInDate.isSame(selectedDate, 'day')) return;

            total++;

            // Determine if check-in is on time
            let isOnTime = true;
            if (checkIn.shift) {
                const checkInTime = dayjs(checkIn.checkInAt);
                const shiftTime = dayjs(checkIn.shift);
                isOnTime = checkInTime.isBefore(shiftTime, 'minute') || checkInTime.isSame(shiftTime, 'minute');
            }

            // Calculate distance
            let isOutOfRange = false;
            if (checkIn.checkInPosition && checkIn.outletId) {
                try {
                    const [checkInLat, checkInLng] = checkIn.checkInPosition.split(',').map(coord => parseFloat(coord.trim()));
                    const outlet = outlets.find(o => o.id === checkIn.outletId);
                    if (outlet && outlet.gpsLat && outlet.gpsLong && 
                        !isNaN(checkInLat) && !isNaN(checkInLng) &&
                        !isNaN(outlet.gpsLat) && !isNaN(outlet.gpsLong)) {
                        const distance = calculateDistance(
                            checkInLat,
                            checkInLng,
                            outlet.gpsLat,
                            outlet.gpsLong
                        );
                        isOutOfRange = distance > 2;
                    }
                } catch (error) {
                    // Ignore errors in distance calculation
                }
            }

            // Categorize check-in
            // Green: on time AND in range
            // Yellow/Orange: (late OR out of range) or both
            if (isOnTime && !isOutOfRange) {
                successful++;
            } else {
                warnings++;
            }
        });

        return { total, successful, warnings };
    }, [checkIns, generalReportFilter, outlets]);

    // Filter and sort outlet groups (include all groups from outlets, even if not in outletGroups list)
    const filteredAndSortedOutletGroups = React.useMemo(() => {
        // Get all group names from outlets (including "Ungrouped")
        const allGroupNames = new Set(Object.keys(outletsByGroup));
        
        // Create a combined list of outlet groups from both sources
        const combinedGroups = [...outletGroups];
        
        // Add any groups from outlets that aren't in the outletGroups list
        allGroupNames.forEach(groupName => {
            const exists = outletGroups.some(g => g.groupName === groupName);
            if (!exists) {
                // Create a temporary group object for unlisted groups
                combinedGroups.push({
                    id: `temp-${groupName}`,
                    groupName: groupName,
                    companyId: undefined,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                });
            }
        });
        
        let filtered = combinedGroups.filter((group) => {
            // Only show groups that have outlets
            if (!outletsByGroup[group.groupName] || outletsByGroup[group.groupName].length === 0) {
                return false;
            }
            if (!filterQuery) return true;
            return group.groupName.toLowerCase().includes(filterQuery.toLowerCase());
        });

        filtered.sort((a, b) => {
            let aValue: string | number;
            let bValue: string | number;

            if (sortField === 'groupName') {
                aValue = a.groupName || '';
                bValue = b.groupName || '';
            } else if (sortField === 'createdAt') {
                aValue = new Date(a.createdAt).getTime();
                bValue = new Date(b.createdAt).getTime();
            } else {
                aValue = new Date(a.updatedAt).getTime();
                bValue = new Date(b.updatedAt).getTime();
            }

            if (sortField === 'groupName') {
                const aStr = String(aValue);
                const bStr = String(bValue);
                return sortOrder === 'asc' 
                    ? aStr.localeCompare(bStr)
                    : bStr.localeCompare(aStr);
            } else {
                return sortOrder === 'asc'
                    ? (aValue as number) - (bValue as number)
                    : (bValue as number) - (aValue as number);
            }
        });

        return filtered;
    }, [outletGroups, outletsByGroup, filterQuery, sortField, sortOrder]);

    return (
        <ProtectedRoute>
            <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
                <div>
                            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-3">
                                <div className="relative inline-flex">
                                    <Lucide
                                        icon="Calendar"
                                        className="absolute group-[.mode--light]:!text-slate-200 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]"
                                    />
                                    <Litepicker
                                        value={generalReportFilter}
                                        onChange={(e) => {
                                            setGeneralReportFilter(e.target.value);
                                        }}
                                        options={{
                                            autoApply: false,
                                            singleMode: true,
                                            showWeekNumbers: true,
                                            dropdowns: {
                                                minYear: 1990,
                                                maxYear: null,
                                                months: true,
                                                years: true,
                                            },
                                        }}
                                        className="w-full md:w-44 sm:w-48 pl-9 rounded-[0.5rem] group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                                    />
                                </div>
                                <div className="flex flex-wrap items-center gap-3">
                                    <div className="relative inline-flex flex-1 md:flex-none">
                                        <Lucide
                                            icon="Clock"
                                            className="absolute group-[.mode--light]:!text-slate-200 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowShiftTimeModal(true)}
                                            className="w-full md:w-44 sm:w-48 pl-9 pr-3 py-2 rounded-[0.5rem] group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box text-left text-sm"
                                        >
                                            Edit Shift Time
                                        </button>
                                    </div>
                                    <Menu>
                                        <Menu.Button
                                            className="relative inline-flex flex-1 md:flex-none"
                                        >
                                            <Lucide
                                                icon="Filter"
                                                className="absolute group-[.mode--light]:!text-slate-200 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]"
                                            />
                                            <button
                                                type="button"
                                                className="w-full md:w-44 sm:w-48 pl-9 pr-3 py-2 rounded-[0.5rem] group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box text-left text-sm"
                                            >
                                                Filter / Sort
                                            </button>
                                        </Menu.Button>
                                        <Menu.Items className="w-56">
                                            <div className="p-3 border-b border-slate-200/60 dark:border-darkmode-400">
                                                <div className="text-xs font-medium mb-2">Filter</div>
                                                <FormInput
                                                    type="text"
                                                    placeholder="Search outlet groups..."
                                                    value={filterQuery}
                                                    onChange={(e) => setFilterQuery(e.target.value)}
                                                    className="w-full"
                                                />
                                            </div>
                                            <div className="p-3">
                                                <div className="text-xs font-medium mb-2">Sort By</div>
                                                <FormSelect
                                                    value={sortField}
                                                    onChange={(e) => setSortField(e.target.value as 'groupName' | 'createdAt' | 'updatedAt')}
                                                    className="w-full mb-2"
                                                >
                                                    <option value="groupName">Group Name</option>
                                                    <option value="createdAt">Created Date</option>
                                                    <option value="updatedAt">Updated Date</option>
                                                </FormSelect>
                                                <FormSelect
                                                    value={sortOrder}
                                                    onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                                                    className="w-full"
                                                >
                                                    <option value="asc">Ascending</option>
                                                    <option value="desc">Descending</option>
                                                </FormSelect>
                                            </div>
                                        </Menu.Items>
                                    </Menu>
                                    <div className="relative inline-flex flex-1 md:flex-none">
                                        <Lucide
                                            icon="Printer"
                                            className="absolute group-[.mode--light]:!text-slate-200 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]"
                                        />
                                        <button
                                            type="button"
                                            className="w-full md:w-44 sm:w-48 pl-9 pr-3 py-2 rounded-[0.5rem] group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box text-left text-sm"
                                        >
                                            Export to Excel
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="p-3 box box--stacked">
                                <div className="flex flex-col lg:items-center lg:flex-row gap-y-5">
                                </div>
                                <div className="py-2 my-2 border border-dashed rounded-md border-slate-300/70">
                                    <div className="flex flex-col md:flex-row">
                                        <div className="flex items-center justify-center flex-1 py-1 border-dashed md:border-r border-slate-300/70 last:border-0">
                                            <div
                                                className={clsx([
                                                    "group flex items-center justify-center w-10 h-10 border rounded-full mr-5",
                                                    "[&.primary]:border-primary/10 [&.primary]:bg-primary/10",
                                                    "[&.success]:border-success/10 [&.success]:bg-success/10",
                                                    ["primary", "success"][_.random(0, 1)],
                                                ])}
                                            >
                                                <Lucide
                                                    icon="Users"
                                                    className={clsx([
                                                        "w-5 h-5",
                                                        "group-[.primary]:text-primary group-[.primary]:fill-primary/10",
                                                        "group-[.success]:text-success group-[.success]:fill-success/10",
                                                    ])}
                                                />
                                            </div>
                                            <div className="flex flex-col flex-start">
                                                <div className="text-slate-500">Total Check In</div>
                                                <div className="flex items-center mt-1.5">
                                                    <div className="text-base font-medium">{checkInStats.total.toLocaleString()}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-center flex-1 py-1 border-dashed md:border-r border-slate-300/70 last:border-0">
                                            <div className="flex items-center justify-center w-10 h-10 border rounded-full mr-5 border-success/10 bg-success/10">
                                                <Lucide
                                                    icon="CheckCircle"
                                                    className="w-5 h-5 text-success"
                                                />
                                            </div>
                                            <div className="flex flex-col flex-start">
                                                <div className="text-slate-500">Successful Check In</div>
                                                <div className="flex items-center mt-1.5">
                                                    <div className="text-base font-medium">{checkInStats.successful.toLocaleString()}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-center flex-1 py-1 border-dashed md:border-r border-slate-300/70 last:border-0">
                                            <div className="flex items-center justify-center w-10 h-10 border rounded-full mr-5 border-warning/10 bg-warning/10">
                                                <Lucide
                                                    icon="AlertTriangle"
                                                    className="w-5 h-5 text-warning"
                                                />
                                            </div>
                                            <div className="flex flex-col flex-start">
                                                <div className="text-slate-500">Check In Warning</div>
                                                <div className="flex items-center mt-1.5">
                                                    <div className="text-base font-medium">{checkInStats.warnings.toLocaleString()}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-x-6 mt-3.5" style={{ rowGap: 0 }}>
                                {loadingOutletGroups || loadingOutlets || loadingCheckIns ? (
                                    <div className="col-span-12 text-center py-8 text-slate-500">
                                        Loading...
                                    </div>
                                ) : filteredAndSortedOutletGroups.length === 0 ? (
                                    <div className="col-span-12 text-center py-8 text-slate-500">
                                        No outlet groups found
                                    </div>
                                ) : (
                                    filteredAndSortedOutletGroups.map((outletGroup) => {
                                        const groupOutlets = outletsByGroup[outletGroup.groupName] || [];
                                        const teamColumns = teamColumnsByGroup.get(outletGroup.groupName) || [];
                                        return (
                                            <div
                                                key={outletGroup.id}
                                                className="flex flex-col col-span-12 md:col-span-6"
                                            >
                                                <div className="p-5 box box--stacked h-full">
                                                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2 border-b border-slate-200/60 dark:border-darkmode-400 pb-2 gap-2">
                                                    <div className="text-base font-medium">
                                                        {outletGroup.groupName}
                                                    </div>
                                                    {teamColumns.length > 0 && (
                                                        <div className="flex items-center gap-2 overflow-x-auto md:overflow-visible pb-1 md:pb-0 -mx-2 md:mx-0 px-2 md:px-0">
                                                            {teamColumns.map((teamCol) => (
                                                                Array.from({ length: teamCol.maxCount }, (_, i) => i + 1).map((num) => (
                                                                    <div key={`${teamCol.teamId}-${num}`} className="text-[11px] font-medium text-slate-500 min-w-[4.5rem] md:w-[4.5rem] text-center flex-shrink-0">
                                                                        {teamCol.teamName} {num}
                                                                    </div>
                                                                ))
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex flex-col gap-1 min-h-0">
                                                    {groupOutlets.length === 0 ? (
                                                        <div className="text-center py-4 text-xs text-slate-500">
                                                            No outlets found
                                                        </div>
                                                    ) : (
                                                        groupOutlets.map((outlet) => (
                                                            <div
                                                                key={outlet.id}
                                                                className="hover:bg-slate-50 transition-all rounded-lg cursor-pointer px-2 -mx-2 py-1 flex flex-col md:flex-row md:items-center md:justify-between gap-2 dark:hover:bg-darkmode-400"
                                                            >
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="font-medium text-xs">{outlet.name}</div>
                                                                    {outlet.address && (
                                                                        <div className="mt-0.5 text-[10px] text-slate-500 truncate">
                                                                            {outlet.address}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center gap-2 md:ml-2 overflow-x-auto md:overflow-visible pb-1 md:pb-0 -mx-2 md:mx-0 px-2 md:px-0">
                                                                    {teamColumns.map((teamCol) => (
                                                                        Array.from({ length: teamCol.maxCount }, (_, i) => i + 1).map((num) => {
                                                                            const outletTeams = outletTeamUserCounts.get(outlet.id);
                                                                            const teamData = outletTeams?.get(teamCol.teamId);
                                                                            const hasCheckIn = teamData && num <= teamData.count;
                                                                            
                                                                            // Get the check-in data of the user at this position (1-indexed)
                                                                            let checkInTime = null;
                                                                            let isOnTime = true;
                                                                            let isOutOfRange = false;
                                                                            let checkInRecord: CheckInOut | null = null;
                                                                            if (hasCheckIn && teamData.userCheckInData) {
                                                                                const userIds = Array.from(teamData.userCheckInData.keys());
                                                                                const userIndex = num - 1; // Convert to 0-indexed
                                                                                if (userIds[userIndex] !== undefined) {
                                                                                    const userData = teamData.userCheckInData.get(userIds[userIndex]);
                                                                                    if (userData) {
                                                                                        checkInTime = userData.time;
                                                                                        isOnTime = userData.isOnTime;
                                                                                        isOutOfRange = userData.isOutOfRange;
                                                                                        checkInRecord = userData.checkInRecord;
                                                                                    }
                                                                                }
                                                                            }
                                                                            
                                                                            // Determine badge color and display text based on logic:
                                                                            // Green: on time AND in range
                                                                            // Yellow: (late OR out of range) but not both
                                                                            // Orange: late AND out of range
                                                                            let badgeColor = 'bg-success/80'; // Green
                                                                            let displayText = checkInTime || '';
                                                                            let tooltipText = '';
                                                                            
                                                                            if (hasCheckIn && checkInTime) {
                                                                                if (isOnTime && !isOutOfRange) {
                                                                                    // Green: on time AND in range
                                                                                    badgeColor = 'bg-success/80';
                                                                                    displayText = checkInTime;
                                                                                    tooltipText = `${teamCol.teamName} user ${num} checked in at ${checkInTime} (on time, in range)`;
                                                                                } else if (!isOnTime && !isOutOfRange) {
                                                                                    // Yellow: late BUT in range
                                                                                    badgeColor = 'bg-warning/80';
                                                                                    displayText = checkInTime;
                                                                                    tooltipText = `${teamCol.teamName} user ${num} checked in at ${checkInTime} (late, in range)`;
                                                                                } else if (isOnTime && isOutOfRange) {
                                                                                    // Yellow: on time BUT out of range
                                                                                    badgeColor = 'bg-warning/80';
                                                                                    displayText = `${checkInTime}/OR`;
                                                                                    tooltipText = `${teamCol.teamName} user ${num} checked in at ${checkInTime} (on time, out of range >2km)`;
                                                                                } else {
                                                                                    // Orange: late AND out of range
                                                                                    badgeColor = 'bg-danger/80';
                                                                                    displayText = `${checkInTime}/OR`;
                                                                                    tooltipText = `${teamCol.teamName} user ${num} checked in at ${checkInTime} (late, out of range >2km)`;
                                                                                }
                                                                            }
                                                                            
                                                                            return (
                                                                                <div key={`${teamCol.teamId}-${num}`} className="min-w-[4.5rem] md:w-[4.5rem] flex justify-center flex-shrink-0">
                                                                                    {hasCheckIn && checkInTime ? (
                                                                                        <span 
                                                                                            className={`px-2.5 py-1 text-[11px] text-white rounded-lg font-medium border-white/20 border cursor-pointer hover:opacity-80 transition-opacity ${badgeColor}`}
                                                                                            title={tooltipText}
                                                                                            onClick={() => {
                                                                                                if (checkInRecord && checkInRecord.photoUrl) {
                                                                                                    setSelectedCheckInImage(checkInRecord.photoUrl);
                                                                                                    setSelectedCheckInRecord(checkInRecord);
                                                                                                    setShowImageModal(true);
                                                                                                }
                                                                                            }}
                                                                                        >
                                                                                            {displayText}
                                                                                        </span>
                                                                                    ) : (
                                                                                        <div className="text-[11px] text-slate-500">-</div>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                
                {/* Check-in Image Modal */}
                <Dialog open={showImageModal} onClose={() => setShowImageModal(false)} size="lg">
                    <Dialog.Panel>
                        <a
                            onClick={(event: React.MouseEvent) => {
                                event.preventDefault();
                                setShowImageModal(false);
                            }}
                            className="absolute top-0 right-0 mt-3 mr-3 text-slate-400 hover:text-slate-500"
                            href="#"
                        >
                            <Lucide icon="X" className="w-8 h-8" />
                        </a>
                        <div className="p-5">
                            {selectedCheckInImage ? (
                                <>
                                    <img
                                        src={selectedCheckInImage}
                                        alt="Check-in photo"
                                        className="w-full rounded-lg object-contain max-h-[80vh] mb-4"
                                    />
                                    {selectedCheckInRecord && (
                                        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-darkmode-400 space-y-2">
                                            {(selectedUser || selectedCheckInRecord.user) && (
                                                <div className="text-sm">
                                                    <span className="font-medium text-slate-600 dark:text-slate-300">Name:</span>{' '}
                                                    <span className="text-slate-800 dark:text-slate-100">
                                                        {(selectedUser?.firstName || selectedCheckInRecord.user?.firstName || '')} {(selectedUser?.lastName || selectedCheckInRecord.user?.lastName || '')}
                                                    </span>
                                                </div>
                                            )}
                                            {selectedCheckInRecord.checkInAt && (
                                                <div className="text-sm">
                                                    <span className="font-medium text-slate-600 dark:text-slate-300">Check In Time:</span>{' '}
                                                    <span className="text-slate-800 dark:text-slate-100">
                                                        {dayjs(selectedCheckInRecord.checkInAt).format('DD MMM YYYY, HH:mm')}
                                                    </span>
                                                </div>
                                            )}
                                            {selectedCheckInRecord.outlet?.name && (
                                                <div className="text-sm">
                                                    <span className="font-medium text-slate-600 dark:text-slate-300">Outlet Name:</span>{' '}
                                                    <span className="text-slate-800 dark:text-slate-100">
                                                        {selectedCheckInRecord.outlet.name}
                                                    </span>
                                                </div>
                                            )}
                                            {selectedCheckInRecord.checkInPosition && selectedCheckInRecord.outletId && (() => {
                                                // Calculate distance between check-in position and outlet GPS coordinates
                                                const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
                                                    const R = 6371; // Earth's radius in kilometers
                                                    const dLat = (lat2 - lat1) * Math.PI / 180;
                                                    const dLon = (lon2 - lon1) * Math.PI / 180;
                                                    const a = 
                                                        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                                                        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                                                        Math.sin(dLon / 2) * Math.sin(dLon / 2);
                                                    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                                                    return R * c; // Distance in kilometers
                                                };

                                                try {
                                                    // Parse checkInPosition (format: "lat,lng")
                                                    const [checkInLat, checkInLng] = selectedCheckInRecord.checkInPosition.split(',').map(coord => parseFloat(coord.trim()));
                                                    
                                                    // Find the outlet to get its GPS coordinates
                                                    const outlet = outlets.find(o => o.id === selectedCheckInRecord.outletId);
                                                    if (outlet && outlet.gpsLat && outlet.gpsLong && 
                                                        !isNaN(checkInLat) && !isNaN(checkInLng) &&
                                                        !isNaN(outlet.gpsLat) && !isNaN(outlet.gpsLong)) {
                                                        const distance = calculateDistance(
                                                            checkInLat,
                                                            checkInLng,
                                                            outlet.gpsLat,
                                                            outlet.gpsLong
                                                        );
                                                        return (
                                                            <div className="text-sm">
                                                                <span className="font-medium text-slate-600 dark:text-slate-300">Range:</span>{' '}
                                                                <span className="text-slate-800 dark:text-slate-100">
                                                                    {distance.toFixed(2)} km
                                                                </span>
                                                            </div>
                                                        );
                                                    }
                                                } catch (error) {
                                                    console.error('Error calculating distance:', error);
                                                }
                                                return null;
                                            })()}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="p-8 text-center bg-slate-100 rounded-lg dark:bg-darkmode-400">
                                    <Lucide icon="Image" className="w-12 h-12 mx-auto text-slate-400 mb-2" />
                                    <p className="text-slate-500 text-sm">No check-in photo available</p>
                                </div>
                            )}
                        </div>
                    </Dialog.Panel>
                </Dialog>

                {/* Shift Time Modal */}
                <Dialog open={showShiftTimeModal} onClose={() => setShowShiftTimeModal(false)} size="lg">
                    <Dialog.Panel>
                        <Dialog.Title>
                            <h2 className="mr-auto text-base font-medium">Edit Shift Times</h2>
                            <a
                                onClick={(event: React.MouseEvent) => {
                                    event.preventDefault();
                                    setShowShiftTimeModal(false);
                                }}
                                className="text-slate-400 hover:text-slate-500"
                                href="#"
                            >
                                <Lucide icon="X" className="w-5 h-5" />
                            </a>
                        </Dialog.Title>
                        <Dialog.Description className="p-5">
                            <div className="space-y-4">
                                {/* Add New Shift Time */}
                                <div className="border-b border-slate-200 dark:border-darkmode-400 pb-4">
                                    <h3 className="text-sm font-medium mb-3">Add New Shift Time</h3>
                                    <div className="flex items-center gap-2">
                                        <FormInput
                                            type="number"
                                            min="1"
                                            max="12"
                                            value={newShiftTime.hours}
                                            onChange={(e) => setNewShiftTime({ ...newShiftTime, hours: e.target.value })}
                                            className="w-20"
                                            placeholder="09"
                                        />
                                        <span className="text-slate-500">:</span>
                                        <FormInput
                                            type="number"
                                            min="0"
                                            max="59"
                                            value={newShiftTime.minutes}
                                            onChange={(e) => setNewShiftTime({ ...newShiftTime, minutes: e.target.value })}
                                            className="w-20"
                                            placeholder="30"
                                        />
                                        <FormSelect
                                            value={newShiftTime.period}
                                            onChange={(e) => setNewShiftTime({ ...newShiftTime, period: e.target.value })}
                                            className="w-24"
                                        >
                                            <option value="AM">AM</option>
                                            <option value="PM">PM</option>
                                        </FormSelect>
                                        <Button
                                            variant="primary"
                                            onClick={handleAddShiftTime}
                                            disabled={addingShiftTime}
                                            className="ml-2"
                                        >
                                            {addingShiftTime ? 'Adding...' : 'Add'}
                                        </Button>
                                    </div>
                                </div>

                                {/* Existing Shift Times */}
                                <div>
                                    <h3 className="text-sm font-medium mb-3">Existing Shift Times</h3>
                                    {loadingShiftTimes ? (
                                        <div className="text-center py-4 text-slate-500">Loading...</div>
                                    ) : shiftTimes.length === 0 ? (
                                        <div className="text-center py-4 text-slate-500">No shift times added yet</div>
                                    ) : (
                                        <div className="space-y-2 max-h-64 overflow-y-auto">
                                            {shiftTimes.map((shiftTime) => (
                                                <div
                                                    key={shiftTime.id}
                                                    className="flex items-center justify-between p-3 bg-slate-50 dark:bg-darkmode-400 rounded-lg"
                                                >
                                                    <span className="font-medium">{shiftTime.time}</span>
                                                    <Button
                                                        variant="outline-danger"
                                                        onClick={() => handleDeleteShiftTime(shiftTime.id)}
                                                        className="ml-2"
                                                    >
                                                        <Lucide icon="Trash2" className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Dialog.Description>
                        <Dialog.Footer className="text-right">
                            <Button
                                variant="outline-secondary"
                                type="button"
                                onClick={() => setShowShiftTimeModal(false)}
                                className="w-20"
                            >
                                Close
                            </Button>
                        </Dialog.Footer>
                    </Dialog.Panel>
                </Dialog>
            </RoleGate>
        </ProtectedRoute>
    );
}

